import { useMemo } from "react";
import { where } from "firebase/firestore";
import { parseDate } from "utilities/datetime";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import { pickemsContest } from "../models/pickems";
import { useCampaigns } from "../user/useCampaigns";
import { useHasPermission } from "@/features/auth/hooks/usePermission";

const getQuery = (
  canSubmitPromoCashPickems: boolean,
  canSubmitRealCashPickems: boolean,
) => {
  if (canSubmitPromoCashPickems && canSubmitRealCashPickems) {
    return undefined;
  }

  if (canSubmitPromoCashPickems) {
    return [where("promoCash", "==", true)];
  }

  if (canSubmitRealCashPickems) {
    return [where("promoCash", "==", false)];
  }
};

export const usePickemsContests = () => {
  const canSubmitPickems = useHasPermission("submitPickems");
  const canSubmitPromoCashPickems = useHasPermission(
    "submitPickemsAwardingPromoCash",
  );
  const canSubmitRealCashPickems = useHasPermission(
    "submitPickemsAwardingRealCash",
  );

  // get contests only if the user has the canSubmitPickems permission
  // AND the permission for AT LEAST ONE specific contest type
  const key =
    canSubmitPickems && (canSubmitPromoCashPickems || canSubmitRealCashPickems)
      ? "/pickemsContests"
      : null;

  const { codes: userCampaigns } = useCampaigns();
  const { data, isLoading, error } = useFirestore(key, pickemsContest.array(), {
    keyPath: "id",
    query: getQuery(canSubmitPromoCashPickems, canSubmitRealCashPickems),
  });

  const processedContests = useMemo(() => {
    if (!data) return [];

    return data
      .filter((contest) => {
        // 1. filter by status
        const isPendingOrLive = ["pending", "live"]
          .map((s) => s.toUpperCase())
          .includes(contest.status);

        // exclude old contests
        if (!isPendingOrLive) {
          return false;
        }

        // 2. filter by included campaign to hide private contests from non-eligible users
        const contestIncludedCampaigns = contest.includedCampaignIds || [];

        // if a contest has no included campaigns, it's available to everyone unless excluded
        const hasIncludedCampaign =
          contestIncludedCampaigns.length === 0 ||
          contestIncludedCampaigns.some((campaignId) =>
            userCampaigns.includes(campaignId),
          );

        if (!hasIncludedCampaign) {
          return false;
        }

        // 3. filter by excluded campaigns to hide contests from users who belong to an excluded campaign
        const contestExcludedCampaigns = contest.excludedCampaignIds || [];

        // if the contest has any excluded campaigns that match the user's campaigns, it should be filtered out
        const hasExcludedCampaigns = contestExcludedCampaigns.some(
          (campaignId) => userCampaigns.includes(campaignId),
        );

        if (hasExcludedCampaigns) {
          return false;
        }

        return true;
      })
      .map(({ scheduledStartTime, ...contest }) => ({
        ...contest,
        scheduledStartTime: parseDate(scheduledStartTime),
        isCustomPrizePool: contest.hasCustomPrizePool,
        prizePoolHeader: contest.hasCustomPrizePool
          ? contest.customPrizePoolHeader
          : undefined,
        prizePoolDescription: contest.hasCustomPrizePool
          ? contest.customPrizePoolDescription
          : undefined,
        customPrizeTiers: contest.hasCustomPrizePool
          ? contest.customPrizeTiers
          : undefined,
      }));
  }, [data, userCampaigns]);

  // different from useEventCounts which is not filtered by campaign
  const contestsCounts = useMemo(() => {
    return processedContests.reduce(
      (acc, contest) => {
        (contest.sports || []).forEach((slug) => {
          if (slug) {
            const sport = slug.toLowerCase();
            acc[sport] = (acc[sport] || 0) + 1;
          }
        });
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [processedContests]);

  return {
    contests: processedContests,
    isLoading,
    error,
    contestsCounts,
  } as const;
};

export type PickemsContests = ReturnType<typeof usePickemsContests>["contests"];
export type PickemsContestsCounts = ReturnType<
  typeof usePickemsContests
>["contestsCounts"];
